import { arrayMove } from '@dnd-kit/helpers';
import {
  DragDropProvider,
  DragOverlay,
  useDraggable,
  useDroppable,
} from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CSSProperties,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { FromStore, GenerationType } from '@cartwhl/common/types';

import Loading from '../atoms/Loading';
import Slider from '../atoms/Slider';
import Tooltip from '../atoms/Tooltip';
import Text from '../base/Text';
import useElementSize from '../hooks/useElementSize';
import useMediaQuery from '../hooks/useMediaQuery';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import CrossIcon from '../icons/CrossIcon';
import EyeIcon from '../icons/EyeIcon';
import Button from '../input/Button';
import { WIZARD_TARGET_ATTRIBUTE } from '../wizard/constants';
import { useWizardPointerAlignEmphasized } from '../wizard';
import './Timeline.css';

export type TimelineData = {
  [timelineId: string]: TimelineItem[];
};

export type TimelineItem = {
  id: string; // Generated by the frontend
  jobID: string;
  title: string;
  trimStart?: number | null;
  trimEnd?: number | null;
  variationNumber?: number;
  comicMp4URL?: string;
  generationType?: GenerationType;
  originalBvhURL?: string;
  stitchStartDelay?: number;
  stitchEndDelay?: number;
  duration: number; // derived from BVH
  startValue: number; // derived from sequence and duration
};

export type DragData = {
  id: string;
  jobID: string;
  timelineId: string;
  title: string;
  duration: number;
  initialPosition: {
    x: number;
    y: number;
  };
  index?: number; // Present for timeline items, absent for custom items
};

type TimelineProps = {
  id: string;
  objectID: string;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  onEditClick?: (jobID: string) => void;
  onHideClick?: (jobID: string) => void;
  wizardTargetId?: string;
  wizardLastClipMenuTargetId?: string;
};

type TimelineContextType = {
  activeDragData: DragData | null;
  timelineData: FromStore<TimelineData> | null;
  stitching: boolean; // Backend processing
  disabled?: boolean;
  setFocusedItem: (item: { timelineId: string; index: number } | null) => void;
};

const TimelineContext = createContext<TimelineContextType | null>(null);

export const useTimelineContext = (): TimelineContextType => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error('Timeline.Item must be used within Timeline.Container');
  }
  return context;
};

export function TimelineProvider({
  children,
  onChange,
  onDuplicate,
  onRemove,
  onTriggerStitching,
  stitching,
  timelineData,
  disabled,
}: {
  children: ReactNode;
  onChange: (timelineId: string, items: TimelineItem[]) => void;
  onDuplicate?: (timelineId: string, index: number) => void;
  onRemove?: (timelineId: string, index: number) => void;
  onTriggerStitching?: (
    timelineId: string,
    items: TimelineItem[],
  ) => Promise<void>;
  stitching: boolean;
  timelineData: FromStore<TimelineData> | null;
  disabled?: boolean;
}) {
  const [activeDragData, setActiveDragData] = useState<DragData | null>(null);

  // Cloned items are used in case a drag is canceled. The state before the drag started can be easily restored
  const [previousItemIds, setPreviousItemIds] = useState<string[] | null>(null);

  const [focusedItem, setFocusedItem] = useState<{
    timelineId: string;
    index: number;
  } | null>(null);

  useHotkeys(
    'meta+d',
    () => {
      if (onDuplicate && focusedItem !== null) {
        onDuplicate(focusedItem.timelineId, focusedItem.index);
      }
    },
    {
      preventDefault: true,
      enabled: !!onDuplicate && focusedItem !== null,
    },
  );

  useHotkeys(
    'backspace',
    () => {
      if (onRemove && focusedItem !== null) {
        onRemove(focusedItem.timelineId, focusedItem.index);
      }
    },
    {
      preventDefault: true,
      enabled: !!onRemove && focusedItem !== null,
    },
  );

  return (
    <TimelineContext.Provider
      value={{
        activeDragData,
        timelineData,
        stitching,
        disabled,
        setFocusedItem,
      }}
    >
      <DragDropProvider
        onDragStart={({ operation }) => {
          if (!timelineData) return;

          const { source, position } = operation;

          if (source) {
            const dragData = {
              ...source.data,
              id: source.id.toString(),
              initialPosition: position.initial,
            } as DragData;
            setActiveDragData(dragData);
            setPreviousItemIds(
              timelineData[source.data.timelineId].map((item) => item.id),
            );
          }
        }}
        onDragOver={({ operation }) => {
          if (!timelineData) return;
          const { source, target } = operation;
          if (!source) return;
          if (target?.id === 'add-to-end-dropzone') {
            console.log('over add to end dropzone');
            return;
          }
          const timelineId = source.data.timelineId || '';

          const currentItems = timelineData[timelineId];
          const isItemInTimeline = currentItems?.some(
            (item) => item.id === String(source.id),
          );

          const sourceItem: TimelineItem = {
            id: crypto.randomUUID(),
            jobID: source.data.jobID,
            title: source.data.title,
            duration: source.data.duration,
            startValue: 0,
            generationType: source.data.generationType,
            originalBvhURL: source.data.originalBvhURL,
          };
          const isNewItem = !previousItemIds?.some(
            (itemId) => itemId === sourceItem.id,
          );
          if (!target && isNewItem) {
            // If source but no target, remove item from timeline
            // Unless it was part of the timeline already
            onChange(
              timelineId,
              timelineData[timelineId].filter(
                (item) => item.id !== sourceItem.id,
              ),
            );
            return;
          }
          // Check if target is the timeline itself OR a timeline item belonging to this timeline
          const isOverTimeline =
            target?.id === timelineId ||
            (target?.data && target.data.timelineId === timelineId);

          if (!isItemInTimeline && isOverTimeline) {
            // If hovering over a timeline item, insert at that position
            if (target?.data && typeof target.data.index === 'number') {
              const insertIndex = target.data.index;
              const updatedItems = [
                ...currentItems.slice(0, insertIndex),
                sourceItem,
                ...currentItems.slice(insertIndex),
              ];
              onChange(timelineId, updatedItems);
            } else {
              // If hovering over empty timeline area, append to end
              // onChange(timelineId, [...currentItems, sourceItem]);
            }
          }
        }}
        onDragEnd={async (event) => {
          if (!timelineData) return;

          const { operation } = event;
          const { source, target, canceled } = operation;

          const myPrevItems = previousItemIds;

          setActiveDragData(null);
          setPreviousItemIds(null);

          //// CANCEL
          if (canceled) {
            console.log('canceled', source);
            return;
          }

          if (!source) return;

          const timelineId = source.data.timelineId || '';
          const alreadyInTimeline = timelineData[timelineId]?.some(
            (item) => item.id === source.id,
          );

          let newItems: TimelineItem[] = [];

          if (
            target &&
            target.id === 'add-to-end-dropzone' &&
            !alreadyInTimeline
          ) {
            //// DROPZONE
            // Add new item to timeline using data from the draggable
            const id = source.id.toString();
            const { title, duration } = source.data || {};
            if (id && title && duration) {
              newItems = [
                ...timelineData[timelineId],
                {
                  ...source.data,
                  id,
                  jobID: source.data.jobID,
                  title,
                  duration,
                  startValue: 0,
                },
              ];
            }
            return;
          } else if (isSortable(source)) {
            // Sorting
            // Info: https://github.com/clauderic/dnd-kit/issues/1815
            const oldIndex = source.sortable.initialIndex;
            const newIndex = source.sortable.index;
            newItems = arrayMove(
              timelineData[timelineId] as TimelineItem[],
              oldIndex,
              newIndex,
            );
          }

          //// SAVE
          onChange(timelineId, newItems);

          //// STITCHING - only trigger if order has changed
          if (onTriggerStitching && myPrevItems) {
            // Check if the order has changed by comparing IDs
            const orderChanged =
              myPrevItems.length !== newItems.length ||
              myPrevItems.some(
                (itemId, index) => itemId !== newItems[index].id,
              );

            if (orderChanged) {
              onTriggerStitching(timelineId, newItems);
            }
          }
        }}
      >
        {children}
      </DragDropProvider>
    </TimelineContext.Provider>
  );
}

export default function Timeline(props: TimelineProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  if (isMobile) {
    return <TimelineMobile {...props} />;
  }
  return <TimelineDesktop {...props} />;
}

export function TimelineDesktop({
  id,
  currentTime,
  onEditClick,
  onHideClick,
  wizardTargetId,
  wizardLastClipMenuTargetId,
}: Omit<TimelineProps, 'disabled' | 'setCurrentTime' | 'objectID'>) {
  const { activeDragData, timelineData, stitching, disabled } =
    useTimelineContext();

  const { ref } = useDroppable({ id });
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementSize(containerRef);

  // Calculate total duration
  const totalDuration = useMemo(() => {
    if (!timelineData || !timelineData[id]) return 0;
    let total = 0;
    timelineData[id].forEach((item) => {
      total += item.duration ?? 0;
    });
    return total;
  }, [timelineData, id]);

  if (!timelineData || !timelineData[id]) return null; // TODO: Show loading state?

  const timelineItems = timelineData[id];
  const gapWidth = (timelineItems.length - 1) * 4;
  const containerWidthMinusGap = containerWidth - gapWidth;

  // Try to find the active item in the timeline, otherwise use drag data
  let activeItem: TimelineItem | null = null;

  if (activeDragData) {
    const found = timelineItems.find((item) => item.id === activeDragData.id);
    if (found) {
      activeItem = found;
    } else {
      activeItem = {
        id: activeDragData.id,
        jobID: activeDragData.jobID,
        title: activeDragData.title,
        duration: activeDragData.duration,
        startValue: 0,
      };
    }
  }

  return (
    <div>
      <div ref={ref} className="relative">
        {/* {isDropTarget && (
          <div className="border-gray-45 dark:border-gray-60 pointer-events-none absolute -inset-5 z-10 rounded-2xl border-2 border-dashed" />
        )} */}
        <div
          className={`bg-loading-backdrop absolute -inset-x-2 -inset-y-2 z-10 flex items-center justify-center rounded-2xl ${
            stitching ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <Loading
            className="text-green-35 h-8 w-8"
            trackClassName="stroke-gray-15"
          />
        </div>

        <div
          className="flex gap-1 rounded-lg"
          ref={containerRef}
          {...(wizardTargetId
            ? { [WIZARD_TARGET_ATTRIBUTE]: wizardTargetId }
            : {})}
        >
          {timelineItems.map((item, index) => {
            return (
              <Item
                key={item.id}
                index={index}
                {...item}
                disabled={disabled}
                currentTime={currentTime}
                totalDuration={totalDuration}
                totalItems={timelineItems.length}
                timelineId={id}
                containerWidth={containerWidthMinusGap}
                onHideClick={
                  onHideClick ? () => onHideClick(item.jobID) : undefined
                }
                onEditClick={
                  onEditClick ? () => onEditClick(item.jobID) : undefined
                }
                wizardLastClipMenuTargetId={
                  index === timelineItems.length - 1
                    ? wizardLastClipMenuTargetId
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>
      <DragOverlay>
        {activeItem ? (
          <Item
            {...activeItem}
            index={0}
            startValue={0}
            currentTime={0}
            totalDuration={totalDuration}
            totalItems={timelineItems.length}
            timelineId={id}
            containerWidth={containerWidthMinusGap}
            isDragOverlay
            // style={{
            //   position: 'fixed',
            //   top: activeDragData?.initialPosition.y,
            //   left: activeDragData?.initialPosition.x,
            // }}
          />
        ) : null}
      </DragOverlay>
    </div>
  );
}

export function TimelineMobile({
  id,
  setCurrentTime,
  currentTime,
}: {
  id: string;
  setCurrentTime: (time: number) => void;
  currentTime: number;
}) {
  const [open, setOpen] = useState(false);
  const { activeDragData, timelineData } = useTimelineContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementSize(containerRef);

  const totalDuration = useMemo(() => {
    if (!timelineData) return 0;
    let total = 0;
    timelineData[id].forEach((item) => {
      total += item.duration ?? 0;
    });
    return total;
  }, [timelineData, id]);

  if (!timelineData) return null;

  const timelineItems = timelineData[id];

  const gapWidth = (timelineItems.length - 1) * 4;
  const containerWidthMinusGap = containerWidth - gapWidth;

  // Try to find the active item in the timeline, otherwise use drag data
  let activeItem: TimelineItem | null = null;

  if (activeDragData) {
    const found = timelineItems.find((item) => item.id === activeDragData.id);
    if (found) {
      activeItem = found;
    } else {
      activeItem = {
        id: activeDragData.id,
        jobID: activeDragData.jobID,
        title: activeDragData.title,
        duration: activeDragData.duration,
        startValue: 0,
      };
    }
  }

  const currentItem = timelineItems.find((item) => {
    if (item.startValue === undefined) return false;
    const endValue = item.startValue + item.duration;
    return currentTime >= item.startValue && currentTime <= endValue;
  });

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <div className="relative">
            <button className="border-primary hover:bg-secondary flex h-[72px] w-full cursor-pointer items-center gap-4 rounded-2xl border px-4 pt-2 transition-colors">
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex gap-1" ref={containerRef}>
                  {timelineItems.map((item, index) => (
                    <Piece
                      key={item.id}
                      timelineId={id}
                      index={index}
                      currentTime={currentTime}
                      duration={item.duration}
                      totalDuration={totalDuration}
                      containerWidth={containerWidthMinusGap}
                    />
                  ))}
                </div>
                <Text
                  variant="studio/body/medium/xs"
                  className="text-secondary h-5"
                >
                  {currentItem?.title}
                </Text>
              </div>
              <ChevronDownIcon className="flex-none" />
            </button>
            <div
              className="absolute left-4 right-[52px] top-[14px]"
              onClick={(e) => e.stopPropagation()}
            >
              <Slider
                currentValue={currentTime}
                onChange={(value) => {
                  setCurrentTime(value);
                }}
                step={1 / 24}
                max={totalDuration}
                min={0}
                trackColor="transparent"
                rangeColor="transparent"
                size="sm"
              />
            </div>
          </div>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="sheet-overlay bg-gray-90/30 fixed inset-0 z-50" />
          <Dialog.Content className="sheet-content focus:outline-hidden bg-primary fixed inset-x-0 bottom-0 z-50 flex h-[84vh] flex-col rounded-t-[20px] shadow-[0_-4px_24px_rgba(0,0,0,0.15)] dark:shadow-[0_-4px_24px_rgba(0,0,0,0.5)]">
            <header className="flex items-center justify-between p-4 pb-2">
              <Dialog.Title asChild>
                <Text as="h2" variant="studio/title/lg">
                  Edit motions
                </Text>
              </Dialog.Title>
              <Button
                variant="tertiary"
                icon={<CrossIcon />}
                onClick={() => setOpen(false)}
              />
            </header>
            {/* Content */}
            <div className="styled-scrollbar flex-1 overflow-y-auto px-4 pb-6">
              <div className="flex flex-col gap-2">
                {timelineData[id].map((item, index) => (
                  <MobileItem
                    key={item.id}
                    {...item}
                    index={index}
                    timelineId={id}
                  />
                ))}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <DragOverlay>
        {activeItem ? (
          <MobileItem {...activeItem} index={0} timelineId={id} isDragOverlay />
        ) : null}
      </DragOverlay>
    </>
  );
}

const Piece = ({
  timelineId,
  style,
  index,
  currentTime,
  duration,
  totalDuration,
  containerWidth,
}: {
  timelineId: string;
  style?: CSSProperties;
  index: number;
  currentTime: number;
  duration: number;
  totalDuration: number;
  containerWidth: number;
}) => {
  const { timelineData } = useTimelineContext();

  const widthInPixels =
    totalDuration > 0 && containerWidth > 0
      ? (duration / totalDuration) * containerWidth
      : 10; // fallback minimum width

  const progress = useMemo(() => {
    if (!timelineData) return 0;
    const timelineItems = timelineData[timelineId];
    const item = timelineItems[index];
    if (item.startValue === undefined) return 0;

    const endValue = item.startValue + duration;

    if (currentTime <= item.startValue) {
      return 0;
    }
    if (currentTime >= endValue) {
      return 100;
    }

    return ((currentTime - item.startValue) / duration) * 100;
  }, [currentTime, duration, timelineData, timelineId, index]);

  const itemStyle = {
    ...style,
    width: `${widthInPixels}px`,
    background: `linear-gradient(to right, var(--gradient-start) 0%, var(--gradient-start) ${progress}%, var(--gradient-end) ${progress}%, var(--gradient-end) 100%)`,
  };

  return (
    <div
      style={itemStyle}
      className="h-[3px] rounded-full [--gradient-end:#CACACA] [--gradient-start:var(--color-blue)] dark:[--gradient-end:#B1D2FF] dark:[--gradient-start:#89B6F5]"
    />
  );
};

const Item = ({
  id,
  index,
  title,
  duration,
  startValue,
  currentTime,
  totalDuration,
  totalItems,
  timelineId,
  containerWidth,
  isDragOverlay,
  onHideClick,
  onEditClick,
  style,
  disabled = false,
  wizardLastClipMenuTargetId,
}: TimelineItem & {
  index: number;
  timelineId: string;
  currentTime: number;
  totalDuration: number;
  totalItems: number;
  containerWidth: number;
  isDragOverlay?: boolean;
  onHideClick?: () => void;
  onEditClick?: () => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  wizardLastClipMenuTargetId?: string;
}) => {
  const { ref, isDragging, sortable } = useSortable({
    id,
    index,
    data: { timelineId, title, duration, index },
  });

  const { setFocusedItem } = useTimelineContext();

  const emphasizeClipMenu = useWizardPointerAlignEmphasized(
    wizardLastClipMenuTargetId,
  );

  sortable.disabled = disabled;

  const MIN_WIDTH = 92;
  const allocatableWidth = containerWidth - totalItems * MIN_WIDTH;
  const widthInPixels =
    totalDuration > 0 && containerWidth > 0
      ? (duration / totalDuration) * allocatableWidth + MIN_WIDTH
      : 150; // fallback minimum width
  const itemStyle = { ...style, width: `${widthInPixels}px` };

  const progress = useMemo(() => {
    if (startValue === undefined) return 0;

    const endValue = startValue + duration;

    if (currentTime <= startValue) {
      return 0;
    }
    if (currentTime >= endValue) {
      return 100;
    }
    return ((currentTime - startValue) / duration) * 100;
  }, [currentTime, duration, startValue]);

  if (isDragging && !isDragOverlay) {
    return (
      <div
        className="h-8 flex-none rounded-lg bg-transparent"
        style={itemStyle}
      >
        <div className="border-gray-45 dark:border-gray-60 h-full w-full rounded-lg border-2 border-dashed" />
      </div>
    );
  }

  return (
    <div ref={ref} tabIndex={-1} className="focus:outline-hidden">
      <div
        tabIndex={0}
        role="button"
        className={`h-8 flex-none rounded-lg ${
          disabled
            ? 'cursor-default [--gradient-end:var(--color-gray-30)] [--gradient-start:var(--color-gray-30)] dark:[--gradient-end:var(--color-gray-45)] dark:[--gradient-start:var(--color-gray-45)]'
            : 'focus:ring-blue cursor-grab [--gradient-end:#ECF4FF] [--gradient-start:#D9E9FF] focus:ring-2 dark:[--gradient-end:#B1D2FF] dark:[--gradient-start:#89B6F5]'
        }`}
        style={{
          background: `linear-gradient(to right, var(--gradient-start) 0%, var(--gradient-start) ${progress}%, var(--gradient-end) ${progress}%, var(--gradient-end) 100%)`,
          boxShadow: isDragOverlay ? 'var(--shadow-menu)' : undefined,
          ...itemStyle,
        }}
        onFocus={() => setFocusedItem({ timelineId, index })}
        onBlur={() => setFocusedItem(null)}
      >
        <div className="dark:text-gray-90 flex h-full select-none items-center gap-1 px-2">
          <DragIcon className="mr-1 h-[15px] flex-none" />
          <div className="min-w-0 flex-1">
            <Text
              as="div"
              variant="studio/body/medium/xs"
              className="w-full min-w-0 truncate pt-[0.2em]"
            >
              {title}

              <span className="text-gray-45 dark:text-gray-60">
                <span className="bg-gray-45 dark:bg-gray-60 mx-2 inline-block h-1 w-1 rounded-full align-middle" />
                {Math.round(duration * 100) / 100}s
              </span>
            </Text>
          </div>
          <div
            className="flex flex-none gap-2"
            onKeyDownCapture={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            {onHideClick && (
              <Tooltip text="Hide motion" disabled={disabled}>
                <button
                  disabled={disabled}
                  className={`text-gray-45 flex h-5 w-5 items-center justify-center rounded-[4px] ${
                    disabled
                      ? 'cursor-default'
                      : 'hover:bg-gray-90/10 hover:text-gray-60 cursor-pointer transition-colors duration-100'
                  }`}
                  onClick={onHideClick}
                >
                  <EyeIcon className=" h-4 w-4" />
                </button>
              </Tooltip>
            )}
            {onEditClick && (
              <Tooltip
                text="Edit motion"
                disabled={disabled}
                open={emphasizeClipMenu ? true : undefined}
              >
                <button
                  type="button"
                  disabled={disabled}
                  className={`text-gray-45 flex h-5 w-5 items-center justify-center rounded-[4px] ${
                    disabled
                      ? 'cursor-default'
                      : `hover:bg-gray-90/10 hover:text-gray-60 cursor-pointer transition-colors duration-100${
                          emphasizeClipMenu
                            ? ' bg-gray-90/10 text-gray-60'
                            : ''
                        }`
                  }`}
                  {...(wizardLastClipMenuTargetId
                    ? { [WIZARD_TARGET_ATTRIBUTE]: wizardLastClipMenuTargetId }
                    : {})}
                  onClick={onEditClick}
                >
                  <ChevronDownIcon className=" h-4 w-4 rotate-180" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileItem = ({
  id,
  title,
  duration,
  index,
  timelineId,
  isDragOverlay,
}: TimelineItem & {
  index: number;
  timelineId: string;
  isDragOverlay?: boolean;
}) => {
  const { ref, isDragging } = useSortable({
    id,
    index,
    data: { timelineId, title, duration, index },
  });

  if (isDragging && !isDragOverlay) {
    return (
      <div ref={ref} className="h-[46px] flex-none rounded-lg bg-transparent">
        <div className="border-primary h-full w-full rounded-lg border-2 border-dashed" />
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className={`bg-secondary flex h-[46px] flex-none items-center justify-between rounded-lg pl-4 pr-3`}
    >
      <div className="flex flex-none gap-3">
        <DragIcon className="h-[15px] flex-none" />
        <Text variant="studio/body/sm">{title}</Text>
      </div>
      <ChevronRightIcon className="flex-none" />
    </div>
  );
};

export const DraggableWrapper = ({
  id,
  jobID,
  title,
  duration,
  timelineId,
  children,
  className,
}: {
  id: string;
  jobID: string;
  title: string;
  duration: number;
  timelineId: string;
  children: ReactNode;
  className?: string;
}) => {
  const { timelineData } = useTimelineContext();
  const timelineItems = timelineData?.[timelineId] || [];
  const isInTimeline = timelineItems.some((item) => item.id === id);

  const { ref, isDragging } = useDraggable({
    id,
    data: {
      timelineId,
      jobID,
      title,
      duration,
    } as DragData,
  });

  return (
    <div
      ref={ref}
      className={className}
      style={
        isInTimeline
          ? {}
          : {
              opacity: isDragging ? 0.5 : 1,
              cursor: 'grab',
            }
      }
    >
      {children}
    </div>
  );
};

export function AddToTheEndDropZone({ timelineId }: { timelineId: string }) {
  const { activeDragData } = useTimelineContext();
  const { ref, isDropTarget } = useDroppable({
    id: 'add-to-end-dropzone',
    data: { timelineId },
  });

  const showDropZone = activeDragData !== null;

  if (!showDropZone) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={`
        h-full min-w-[100px] rounded-lg border-2 border-dashed transition-colors
        ${
          isDropTarget
            ? 'dark:bg-blue/20 dark:border-blue border-blue bg-blue-50'
            : 'border-gray-45 bg-gray-30 dark:border-gray-60 dark:bg-gray-90/50'
        }
      `}
    >
      <div className="flex h-full items-center justify-center">
        <Text variant="studio/body/sm">Drop here to add</Text>
      </div>
    </div>
  );
}

function DragIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 9 15"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M1.5 12C2.32843 12 3 12.6716 3 13.5C3 14.3284 2.32843 15 1.5 15C0.671573 15 0 14.3284 0 13.5C0 12.6716 0.671573 12 1.5 12ZM7.5 12C8.32843 12 9 12.6716 9 13.5C9 14.3284 8.32843 15 7.5 15C6.67157 15 6 14.3284 6 13.5C6 12.6716 6.67157 12 7.5 12ZM1.5 6C2.32843 6 3 6.67157 3 7.5C3 8.32843 2.32843 9 1.5 9C0.671573 9 0 8.32843 0 7.5C0 6.67157 0.671573 6 1.5 6ZM7.5 6C8.32843 6 9 6.67157 9 7.5C9 8.32843 8.32843 9 7.5 9C6.67157 9 6 8.32843 6 7.5C6 6.67157 6.67157 6 7.5 6ZM1.5 0C2.32843 0 3 0.671573 3 1.5C3 2.32843 2.32843 3 1.5 3C0.671573 3 0 2.32843 0 1.5C0 0.671573 0.671573 0 1.5 0ZM7.5 0C8.32843 0 9 0.671573 9 1.5C9 2.32843 8.32843 3 7.5 3C6.67157 3 6 2.32843 6 1.5C6 0.671573 6.67157 0 7.5 0Z"
        fillOpacity="0.4"
      />
    </svg>
  );
}
